import React from 'react';
import { Button } from '@/components/ui/Button';
import { Check, X, Undo, HelpCircle, UserCheck } from 'lucide-react';

interface ApprovalDecisionActionsBarProps {
  onApprove: () => void;
  onReject: () => void;
  onSendBack: () => void;
  onRequestClarification: () => void;
  onDelegate: () => void;
  onAddComment?: () => void;
  canApprove: boolean;
  canDelegate: boolean;
  isProcessing?: boolean;
  approveLabel?: string;
}

export const ApprovalDecisionActionsBar: React.FC<ApprovalDecisionActionsBarProps> = ({
  onApprove,
  onReject,
  onSendBack,
  onRequestClarification,
  onDelegate,
  canApprove,
  canDelegate,
  isProcessing,
  approveLabel = 'Approve Requisition',
}) => {
  return (
    <div className="sticky bottom-0 z-30 w-full bg-card/95 backdrop-blur-md border-t border-border p-4 shadow-xl font-poppins transition-all">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-bold text-foreground font-poppins">
            Authoritative Sign-off: <span className="text-primary font-bold">Pending Your Decision</span>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
          {canDelegate && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDelegate}
              disabled={isProcessing}
              className="h-10 text-xs font-semibold rounded-xl text-purple-700 dark:text-purple-300 border-purple-500/30 hover:bg-purple-500/10 font-poppins"
            >
              <UserCheck className="h-4 w-4 mr-1.5" />
              Delegate
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={onRequestClarification}
            disabled={isProcessing}
            className="h-10 text-xs font-semibold rounded-xl text-primary border-primary/30 hover:bg-primary/10 font-poppins"
          >
            <HelpCircle className="h-4 w-4 mr-1.5" />
            Request Clarification
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={onSendBack}
            disabled={isProcessing}
            className="h-10 text-xs font-semibold rounded-xl text-amber-700 dark:text-amber-300 border-amber-500/30 hover:bg-amber-500/10 font-poppins"
          >
            <Undo className="h-4 w-4 mr-1.5" />
            Send Back
          </Button>

          {canApprove && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReject}
              disabled={isProcessing}
              className="h-10 text-xs font-semibold rounded-xl text-rose-700 dark:text-rose-300 border-rose-500/30 hover:bg-rose-500/10 font-poppins"
            >
              <X className="h-4 w-4 mr-1.5" />
              Reject Request
            </Button>
          )}

          {canApprove && (
            <Button
              size="sm"
              onClick={onApprove}
              isLoading={isProcessing}
              className="h-10 px-6 text-xs font-bold rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-md hover:shadow-emerald-500/25 border-0 font-poppins cursor-pointer"
            >
              <Check className="h-4 w-4 mr-1.5" />
              {approveLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
